import { useState, useMemo } from 'react';
import {
  Button,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import useReactHookFormProps from '../../../../hooks/useReactHookFormProps';
import messages from './messages';
import useIntl from '../../../../hooks/useIntl';
import { getFormFields } from './constants';
import useSelectedProperty from '../../../../hooks/useSelectedProperty';
import moment from 'moment';
import useStatesQuery from '../../../../hooks/data-fetching/useStatesQuery';
import { DEFAULT_TRACS_VOUCHER_ID } from '../../../../constants/hudVoucher';
import ConditionalTooltip from '../../../../components/ConditionalTooltip';

const PrepareExecutionModal = ({
  onPrepareExecution,
  disabled,
  disabledTooltip,
  voucher,
  initialValues = null,
  isSubmitting: isSubmittingProp = false,
}) => {
  const [open, setOpen] = useState(false);
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const { data: states, isLoading: isStatesLoading } = useStatesQuery();

  const voucherMonthMoment = moment(voucher.date).startOf('month');
  const isResubmission = useMemo(
    () => voucher.submissionStatus === 'Draft' && !!voucher.lastSubmissionDate,
    [voucher.submissionStatus, voucher.lastSubmissionDate],
  );

  const defaultValues = useMemo(() => {
    const haveOldExecutionDate =
      initialValues?.date &&
      moment(initialValues?.date).isValid() &&
      moment(initialValues?.date).isBefore(moment(), 'day');
    // Set date to current date if it's a resubmission with execution date prior to today
    const date =
      !initialValues?.date || (isResubmission && haveOldExecutionDate)
        ? moment()
        : initialValues.date;
    return {
      name: '',
      title: '',
      voucherId: DEFAULT_TRACS_VOUCHER_ID,
      streetAddress: selectedProperty.physicalStreetAddress1 ?? '',
      city: selectedProperty.physicalCity ?? '',
      state: selectedProperty.physicalState ?? '',
      zipCode: selectedProperty.physicalZip ?? '',
      phoneNumber: (selectedProperty.phoneNumber ?? '').replace(/\D/g, ''),
      ...(initialValues ?? {}),
      date,
    };
  }, [
    initialValues,
    selectedProperty.phoneNumber,
    selectedProperty.physicalCity,
    selectedProperty.physicalState,
    selectedProperty.physicalStreetAddress1,
    selectedProperty.physicalZip,
    isResubmission,
  ]);

  const reactHookFormProps = useReactHookFormProps({
    defaultValues,
    mode: 'onChange',
  });

  const handleClose = () => {
    setOpen(false);
    reactHookFormProps.reset(defaultValues);
  };

  const formFields = useMemo(
    () =>
      getFormFields({
        voucherMonthMoment,
        messages,
        intl,
        states,
        isStatesLoading,
        isResubmission,
        lastSubmissionDate: voucher.lastSubmissionDate,
      }),
    [
      intl,
      isStatesLoading,
      states,
      voucherMonthMoment,
      isResubmission,
      voucher.lastSubmissionDate,
    ],
  );

  const handleSubmit = async (data) => {
    const normalized = {
      ...data,
      name: data.name?.trim?.() ?? '',
      title: data.title?.trim?.() ?? '',
      phoneNumber: (data.phoneNumber ?? '').replace(/\D/g, ''),
      zipCode: String(data.zipCode ?? '')
        .replace(/\D/g, '')
        .slice(0, 5),
    };
    await onPrepareExecution(normalized);
    handleClose();
  };

  const button = (
    <Button
      variant="shout"
      onClick={() => {
        reactHookFormProps.reset(defaultValues);
        setOpen(true);
      }}
      disabled={disabled}
    >
      {intl.formatMessage(messages.modalTitle)}
    </Button>
  );

  return (
    <>
      <ConditionalTooltip
        show={disabled && Boolean(disabledTooltip)}
        title={disabledTooltip}
        placement="top"
      >
        {button}
      </ConditionalTooltip>
      <Modal
        open={open}
        onClose={handleClose}
        title={intl.formatMessage(messages.modalTitle)}
        actionsProps={[
          {
            children: intl.formatMessage(messages.cancel),
            onClick: handleClose,
          },
          {
            children: intl.formatMessage(messages.save),
            submitButton: true,
            disabled: !reactHookFormProps.isValid || isSubmittingProp,
            isSubmitting: reactHookFormProps.isSubmitting || isSubmittingProp,
            onClick: reactHookFormProps.handleSubmit(handleSubmit),
          },
        ]}
        ModalProps={{
          PaperProps: {
            sx: {
              width: {
                xs: 'calc(100% - 32px)',
                sm: 480,
              },
            },
          },
        }}
      >
        <Typography variant="bodyMedium" marginBottom={1.5} display="block">
          {intl.formatMessage(messages.description)}
        </Typography>
        <Form
          ReactHookFormProps={reactHookFormProps}
          containerSX={{ marginBottom: 0 }}
          fields={formFields}
        />
      </Modal>
    </>
  );
};

export default PrepareExecutionModal;
